import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {getHasNewIntegrationsDiscoverabilityP4} from '../../../../store/featureFlags/featureFlagsSelectors';
import {DraggableSwitcherItem, DraggableSwitcherItemPublicProps} from './draggableSwitcherItem';

/*
 * Props.
 */

interface IntegrationSwitcherItemProps extends DraggableSwitcherItemPublicProps {
  integrationName: string;
  integrationIconsUrl: string;
  isInMultiViewButton?: boolean;
}

/*
 * Style.
 */

interface IconStyleProps {
  $isSelected: boolean;
}
const StyledIconDiv = styled.div<IconStyleProps>`
  width: 100%;
  height: 100%;
  ${center()};
`;

const StyledDefaultIconDiv = styled(StyledIconDiv)`
  ${(p) => addDefaultStyle(p.$isSelected)}
`;

function addDefaultStyle(isSelected: boolean) {
  // Hide on selected.
  if (isSelected) {
    return css`
      display: none;
    `;
  }

  // Hide on hover.
  return css`
    *:hover > && {
      display: none;
    }
  `;
}

const StyledHoverIconDiv = styled(StyledIconDiv)`
  ${(p) => addHoverStyle(p.$isSelected)}
`;

function addHoverStyle(isSelected: boolean) {
  // Hide on selected.
  if (isSelected) {
    return css`
      display: none;
    `;
  }

  // Show on hover.
  return css`
    display: none;
    *:hover > && {
      display: flex;
    }
  `;
}

const StyledSelectedIconDiv = styled(StyledIconDiv)`
  /* Hide on non-selected. */
  ${(p) => !p.$isSelected && 'display: none;'}
`;

/*
 * Component.
 */

export const IntegrationSwitcherItem: FC<IntegrationSwitcherItemProps> = (props) => {
  const isNewIntDiscoveryEnabled = useAppSelector(getHasNewIntegrationsDiscoverabilityP4);
  return (
    <DraggableSwitcherItem {...props} renderTooltip={() => props.integrationName}>
      {(isDragging) => renderContent(props, isDragging, isNewIntDiscoveryEnabled)}
    </DraggableSwitcherItem>
  );
};

function renderContent(
  props: IntegrationSwitcherItemProps,
  isDragging: boolean,
  isNewIntDiscoveryEnabled: boolean,
) {
  if (isDragging) {
    return <img alt="integration-hover" src={buildIconUrl(props, 'hover')} />;
  }

  return (
    <>
      <StyledDefaultIconDiv $isSelected={Boolean(props.isSelected)}>
        <img
          alt="integration-default"
          src={buildIconUrl(props, isNewIntDiscoveryEnabled ? 'hover' : 'default')}
        />
      </StyledDefaultIconDiv>
      <StyledHoverIconDiv $isSelected={Boolean(props.isSelected)}>
        <img
          alt="integration-hover"
          // If we are in the multi-view button we do not need to show a hover state.
          src={buildIconUrl(props, props.isInMultiViewButton ? 'default' : 'hover')}
        />
      </StyledHoverIconDiv>
      <StyledSelectedIconDiv $isSelected={Boolean(props.isSelected)}>
        <img
          alt="integration-selected"
          src={buildIconUrl(props, isNewIntDiscoveryEnabled ? 'hover' : 'selected')}
        />
      </StyledSelectedIconDiv>
    </>
  );
}

function buildIconUrl(props: IntegrationSwitcherItemProps, iconName: string) {
  return `${props.integrationIconsUrl}${iconName}.svg`;
}
